import {Box, Tooltip, useTheme} from "@mui/material";
import {ACard, ADataGrid, ADataGridColumn, ADataGridFilter, PagedSearchParams} from "@atiautomacao/ati-ui-library";
import {Delete, MoreHorizOutlined, DeleteOutlined, EditOutlined, AccountCircleOutlined} from "@mui/icons-material";
import ConfirmDialog from "../../../Shared/Components/ConfirmDialog";
import ActionsMenu, { ActionMenuItem } from "../../../Shared/Components/ActionsMenu";
import React, {useCallback, useEffect, useState} from "react";
import {extractFiltersFromColumns} from "../../../Utils/DataUitils";
import IconButton from "@mui/material/IconButton";
import {SystemRoutes} from "../../../Utils/RouteUtils";
import {useNavigate} from "react-router-dom";
import {DeletePowerStationReactQuery, PowerStationSearchReactQuery} from "./PowerStationReactQuery";
import {QueryClient, QueryClientProvider} from "react-query";
import {HeaderMenu} from "../HeaderMenu";
import {PowerStationInfoProps} from "../../../Shared/Types/PowerStation";
import UserAssociationPanel from "./components/UserAssociationPanel";
import {useAppSelector} from "../../../Config/Hooks";
import {hasPermission} from "../../../Shared/Auth/AuthenticationUtil";
import {AUTHORITIES} from "../../../Config/Constants";

const initColumns = [
    {
        name: 'powerStationName',
        label: 'nome',
        align: 'left',
        visibility: true,
        minWidth: 100,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'powerStationName', condition: 'contains', value: '', sort: 'asc'}

    },
    {
        name: 'numberOfUG',
        label: 'N.º de Skids',
        align: "center",
        visibility: true,
        minWidth: 100,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'numberOfUG', condition: 'equals', value: '', sort: 'asc'}

    },
    {
        name: 'phone1',
        label: 'telefone',
        align: 'left',
        visibility: true,
        minWidth: 100,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'phone1', condition: 'contains', value: '', sort: ''}

    },
    {
        name: 'adminName',
        label: 'admin',
        align: 'left',
        visibility: true,
        minWidth: 100,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'adminName', condition: 'contains', value: '', sort: ''}

    },
    {
        name: 'abvStateName',
        label: 'estado',
        align: 'left',
        visibility: true,
        minWidth: 100,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'abvStateName', condition: 'contains', value: '', sort: ''}

    },
    {
        name: 'shortName',
        label: 'sigla',
        align: 'left',
        visibility: true,
        minWidth: 100,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'shortName', condition: 'contains', value: '', sort: ''}

    },
] as ADataGridColumn [];

export type ActionConfig = {
    actionName: string;
    disabled: boolean;
};

export interface RowPowerStation {
    id: number;
    name: string;
    actionsConfig?: Array<ActionConfig>
}

export function PowerStationSettingsPage(){
    const navigate = useNavigate();
    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToViewUsers = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.VIEW_POWER_STATION_USER, AUTHORITIES.SEARCH_POWER_STATION_USER
    ]);
    const isAuthorizedToSavePlant = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SAVE_POWER_PLANT
    ]);
    const isAuthorizedToDeletePlant = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.DELETE_POWER_PLANT
    ]);
    
    const lastMousePosRef = React.useRef({ x: 0, y: 0 });

    const [pagedSearchParams, setPagedSearchParams] = useState(new PagedSearchParams(extractFiltersFromColumns(initColumns), 0, 50));
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    
    const handleOnFiltersChange = useCallback((dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
        let newPageParams: PagedSearchParams = new PagedSearchParams(dataGridFilters, page, pageSize);
        setPagedSearchParams(
            newPageParams
        );
    }, []);

    const handleMouseMove = (e: React.MouseEvent) => {
        lastMousePosRef.current = { x: e.clientX, y: e.clientY };
    };

    const mutation = DeletePowerStationReactQuery(pagedSearchParams);
    const { data, isSuccess } = PowerStationSearchReactQuery(pagedSearchParams);

    const [selectedToDelete, setSelectedToDelete] = useState<any>(null);
    const [totalOfRecords, setTotalOfRecords] = useState(0);
    const [rows, setRows] = useState<Array<RowPowerStation>>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [openUserAssociationPanel, setOpenUserAssociationPanel] = useState(false);
    const [selectedPowerStationForUsers, setSelectedPowerStationForUsers] = useState<any>(null);

    useEffect(() => {
        if(isSuccess) {
            setTotalOfRecords(data?.totalOfRecords);
            if(data?.data){
                let rowsEquipment = data.data.map((powerStation: PowerStationInfoProps) => {
                    return {
                        id: powerStation.id,
                        powerStationId: powerStation.powerStationId,
                        powerStationName: powerStation.powerStationName,
                        numberOfUG: powerStation.numberOfUG,
                        phone1: powerStation.phone1,
                        adminName: powerStation.adminName,
                        abvStateName: powerStation.abvStateName,
                        shortName: powerStation.shortName,
                        cityName: powerStation.cityName,
                        actionsConfig: [
                            {actionName: "edit", disabled: false},
                            {actionName: "delete", disabled: false},
                        ]
                    }
                });
                setRows(rowsEquipment);
            }
        }
    }, [data?.data]);

    const handleEditPowerStationType = useCallback((data: any) => {
        navigate(SystemRoutes.SETTINGS_POWER_PLANT_FORM, { state: { id: data.powerStationId } });
    },[navigate]);

    const handleDeletePowerStationType = useCallback((data: any) => {
        setSelectedToDelete(data.powerStationId);
        setOpenDialog(true);
    },[]);

    const handleOpenUserAssociation = useCallback((row: any) => {
        setSelectedPowerStationForUsers(row);
        setOpenUserAssociationPanel(true);
    }, []);

    const handleCloseActionsMenu = () => {
        setMenuPos(null);
        setSelectedRow(null);
    };

    const handleActionsMenu = (row: any) => {
        setSelectedRow(row);
        setMenuPos({ left: lastMousePosRef.current.x, top: lastMousePosRef.current.y });
    };

    const getActionMenuItems = (): ActionMenuItem[] => {
        if (!selectedRow) return [];
        
        return [
            {
                id: "edit",
                label: "Editar informações",
                icon: <EditOutlined fontSize="small" />,
                onClick: () => handleEditPowerStationType(selectedRow),
                disabled: !isAuthorizedToSavePlant
            },
            {
                id: "associateUsers",
                label: "Associar usuários",
                icon: <AccountCircleOutlined fontSize="small" />,
                onClick: () => handleOpenUserAssociation(selectedRow),
                disabled: !isAuthorizedToViewUsers
            },
            {
                id: "delete",
                label: "Excluir",
                icon: <DeleteOutlined fontSize="small" />,
                onClick: () => handleDeletePowerStationType(selectedRow),
                danger: true,
                dividerBefore: true,
                disabled: !isAuthorizedToDeletePlant
            },
        ];
    };

    const handleDialogConfirm = async () => {
        mutation.mutate({ id: selectedToDelete, pagedSearchParams });
        setSelectedToDelete(null);
        handleDialogToggle();
    }

    useEffect(() => {
        if(mutation.isSuccess) {
            setPagedSearchParams(new PagedSearchParams(extractFiltersFromColumns(initColumns), 0, 100))
        }

    }, [mutation.isSuccess]);

    const handleDialogToggle = () => {
        setOpenDialog(!openDialog);
    }

    const handleDialogClose = () => {
        handleDialogToggle();
    }

    return(
       <>
           <HeaderMenu systemRoutes={SystemRoutes.SETTINGS_POWER_PLANT_FORM} disableSave={!isAuthorizedToSavePlant}/>
           <Box onMouseMove={handleMouseMove}>
               <ACard ariaLabel={"settings-power-station-page"}>
                   <ADataGrid
                       ariaLabel={"settings-grid-power-station"}
                       hideFilters={false}
                       showFilterOptions={false}
                       hideSelection={false}
                       columns={initColumns}
                       rows={rows}
                       page={pagedSearchParams.page}
                       rowsPerPage={pagedSearchParams.size}
                       loading={false}
                       totalOfRecords={totalOfRecords}
                       onFiltersChange={handleOnFiltersChange}
                       actions={
                           <>
                           <span></span>
                             <IconButton 
                                name="actions"
                                size={"small"}
                                aria-label="Ações"
                                onClick={(row: any) => handleActionsMenu(row)}
                            >
                                    <Tooltip title="Opções">
                                        <MoreHorizOutlined/>
                                    </Tooltip>
                                </IconButton>
                           </>
                       }
                   />
               </ACard>
           </Box>
           <ActionsMenu
               open={Boolean(menuPos && selectedRow)}
               onClose={handleCloseActionsMenu}
               menuPos={menuPos}
               items={getActionMenuItems()}
           />
           <ConfirmDialog
            icon={<Delete fontSize="large" color="error"/>}
               title={'Tem certeza que deseja excluir essa usina?'}
               description={'Ao remover essa usina, todos os dados relacionados a ela também serão removidos. Essa ação não poderá ser desfeita'}
               confirmLabel={'Excluir Usina'}
               open={openDialog}
               handleConfirm={handleDialogConfirm}
               handleClose={handleDialogClose}
           />
           <UserAssociationPanel
               open={openUserAssociationPanel}
               onClose={() => setOpenUserAssociationPanel(false)}
               powerStationId={selectedPowerStationForUsers?.powerStationId || 0}
               powerStationName={selectedPowerStationForUsers?.powerStationName || ""}
           />
       </>
    );

}

export function PowerStationHomePage() {
    const queryClient = new QueryClient();

    return (
        <QueryClientProvider client={queryClient}>
            <PowerStationSettingsPage />
        </QueryClientProvider>
    )
}
